import { useCallback, useEffect, useRef, useState } from 'react';
import { Outlet, useRouteError } from 'react-router';
import { useTranslation } from 'react-i18next';
import { ContentLayout, LoadingScreen } from '@nohara-dxdev/react-core';
import { OidcProtectedComponent, useOidc } from '@nohara-dxdev/react-oidc';
import { useNavigate } from 'react-router';

import { AppLayout } from '@/components/layouts';
import { ScrollToTop } from '@/components/scroll-to-top';
import { paths } from '@/config/paths';
import { useSyncOidcRoles } from '@/features/roles';
import { useAuth } from '@/hooks/use-auth';
import { membersService } from '@/features/members/services/members.service';

export const ErrorBoundary = () => {
  const { t } = useTranslation();
  const error = useRouteError();

  console.error(error);

  return (
    <AppLayout>
      <ContentLayout title={t('common.errors.somethingWentWrong')}>Error</ContentLayout>
    </AppLayout>
  );
};

const AppRoot = () => {
  const { isAuthenticated } = useOidc();
  const { mutate: syncRoles } = useSyncOidcRoles();
  const hasSyncedRef = useRef(false);
  const { setUserAuthInfo } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  useEffect(() => {
    // Sync OIDC roles after successful authentication (only once per session)
    if (isAuthenticated && !hasSyncedRef.current) {
      hasSyncedRef.current = true;
      syncRoles(undefined, {
        onSuccess: () => {
          console.warn('OIDC roles synced successfully');
        },
        onError: (error) => {
          console.error('Failed to sync OIDC roles:', error);
        },
      });
    }
  }, [isAuthenticated, syncRoles]);

  const fetchUserAuthInfo = useCallback(async () => {
    try {
      const user = await membersService.getUserAuthInfoByToken();
      setUserAuthInfo(user);
      setIsLoading(false);
    } catch (error) {
      console.error('Failed to fetch user auth info:', error);
      navigate(paths.notAuthorized.path);
    }
  }, [navigate, setUserAuthInfo, setIsLoading]);

  useEffect(() => {
    setIsLoading(true);
    if (isAuthenticated) {
      void fetchUserAuthInfo();
    }
  }, [fetchUserAuthInfo, setIsLoading, isAuthenticated]);

  return (
    <OidcProtectedComponent redirectTo={paths.auth.login.path} fallbackElement={<LoadingScreen />}>
      {isLoading ? (
        <LoadingScreen />
      ) : (
        <>
          <ScrollToTop />
          <AppLayout>
            <Outlet />
          </AppLayout>
        </>
      )}
    </OidcProtectedComponent>
  );
};

export default AppRoot;
